import { PACKAGE_PICKER_COMPONENTS, PACKAGE_PICKER_EVENTS, packagePickerPubSub, useBeforeComponentMount } from '@wix/package-picker-common';
import { FeatureGroup, FeatureSection, ProductsGrid as ProductsGridUi } from '@wix/package-picker-components';
import { Logger } from '@wix/wix-log';
import React from 'react';
import { useProducts, useQueriesHandler } from '../../hooks';
import { PackagePickerApiClient, usePackagePickerType } from '../../providers';
import { AUTONOMOUS_COMPONENT_NAMES } from '../../types';
const COMPONENT_NAME = AUTONOMOUS_COMPONENT_NAMES.PRODUCTS_GRID;
const logger = new Logger(COMPONENT_NAME);
export const ProductsGrid = _ref => {
  var _productsQuery$data, _productFeatureDetail, _productFeatureDetail2, _productFeatureDetail3;
  let {
    renderProductHeader,
    renderFeatureRow,
    renderFeatureGroupHeader,
    renderFeatureSectionHeader,
    collapsibleProps,
    innerRef,
    onInit,
    onLoad,
    onError,
    productsSortType
  } = _ref;
  useBeforeComponentMount(() => packagePickerPubSub.emit(PACKAGE_PICKER_EVENTS.COMPONENT_LOAD_START, {
    componentName: PACKAGE_PICKER_COMPONENTS.PRODUCTS_GRID
  }));
  const gridHeaderRef = React.useRef(null);
  const {
    packagePickerType
  } = usePackagePickerType();
  const {
    useProductFeatureDetails,
    useRecommendedProductFamily,
    useSale
  } = PackagePickerApiClient.hooks;
  const productsQuery = useProducts({
    packagePickerType,
    sortType: productsSortType
  });
  const productIds = ((_productsQuery$data = productsQuery.data) == null ? void 0 : _productsQuery$data.reduce((acc, product) => {
    if (product.productId) {
      return [...acc, product.productId];
    }
    return [...acc];
  }, [])) ?? [];
  const productFeatureDetailsQuery = useProductFeatureDetails({
    packagePickerType,
    productIds
  }, !!(productIds != null && productIds.length));
  const recommendedProductFamilyQuery = useRecommendedProductFamily({
    packagePickerType
  });
  const saleQuery = useSale({
    packagePickerType
  });
  const {
    isSuccess,
    data,
    isError
  } = useQueriesHandler({
    componentName: COMPONENT_NAME,
    callbacks: {
      onLoad,
      onError,
      onInit
    },
    queries: {
      recommendedProductFamilyQuery,
      productsQuery,
      productFeatureDetailsQuery,
      saleQuery
    }
  });
  React.useEffect(() => {
    if (isSuccess || isError) {
      packagePickerPubSub.emit(PACKAGE_PICKER_EVENTS.COMPONENT_LOAD_FINISH, {
        componentName: PACKAGE_PICKER_COMPONENTS.PRODUCTS_GRID,
        data,
        isError
      });
    }
  }, [isSuccess, isError]);
  const groups = Object.values(((_productFeatureDetail = productFeatureDetailsQuery.data) == null ? void 0 : _productFeatureDetail.groups) ?? {}).sort((g1, g2) => g1.order - g2.order);
  const sections = Object.values(((_productFeatureDetail2 = productFeatureDetailsQuery.data) == null ? void 0 : _productFeatureDetail2.sections) ?? {}).sort((s1, s2) => s1.order - s2.order).filter(section => section.displayInDesktop);

  // TODO: Use better open/folded logic
  const openSections = sections.filter(s => s.alwaysOpen);
  const foldedSections = sections.filter(s => !s.alwaysOpen);
  const featuresMap = ((_productFeatureDetail3 = productFeatureDetailsQuery.data) == null ? void 0 : _productFeatureDetail3.features) ?? {};
  const getRecommended = product => {
    var _recommendedProductFa;
    return (_recommendedProductFa = recommendedProductFamilyQuery.data) == null ? void 0 : _recommendedProductFa[product.productFamily];
  };
  const products = productsQuery.data;
  if (!products || !isSuccess) {
    return null;
  }
  const renderFeature = featureId => {
    const feature = featuresMap[featureId];
    if (feature) {
      return renderFeatureRow({
        feature,
        products
      });
    }
    logger.withData({
      featuresMap
    }).debug(`Couldn't find mapping for feature ${featureId} `);
  };
  return /*#__PURE__*/React.createElement(ProductsGridUi, {
    productsCount: productIds.length,
    innerRef: innerRef
  }, /*#__PURE__*/React.createElement(ProductsGridUi.Header, {
    innerRef: gridHeaderRef
  }, products.map(product => renderProductHeader({
    product,
    recommendedContent: getRecommended(product),
    saleData: saleQuery.data
  }))), groups.map(group => {
    var _collapsibleProps$gro, _collapsibleProps$gro2;
    return /*#__PURE__*/React.createElement(FeatureGroup, {
      key: group.name,
      header: renderFeatureGroupHeader({
        group
      }),
      collapsible: collapsibleProps == null || (_collapsibleProps$gro = collapsibleProps.group) == null ? void 0 : _collapsibleProps$gro.collapsible,
      labels: collapsibleProps == null || (_collapsibleProps$gro2 = collapsibleProps.group) == null ? void 0 : _collapsibleProps$gro2.labels
    }, group.featureIds.map(renderFeature));
  }), openSections.map(section => {
    return /*#__PURE__*/React.createElement(FeatureSection, {
      key: section.name,
      isOpen: true
    }, renderFeatureSectionHeader({
      section
    }), /*#__PURE__*/React.createElement(FeatureSection.Features, null, section.featureIds.map(renderFeature)));
  }), /*#__PURE__*/React.createElement(ProductsGridUi.Collapsible, {
    labels: collapsibleProps == null ? void 0 : collapsibleProps.foldedSectionsLabels,
    onToggle: action => collapsibleProps == null || collapsibleProps.onShowMoreFeaturesClick == null ? void 0 : collapsibleProps.onShowMoreFeaturesClick(action)
  }, foldedSections.map(section => {
    return /*#__PURE__*/React.createElement(FeatureSection, {
      key: section.name
    }, renderFeatureSectionHeader({
      section
    }), /*#__PURE__*/React.createElement(FeatureSection.Features, null, section.featureIds.map(renderFeature)));
  })));
};
//# sourceMappingURL=ProductsGrid.js.map